/**
 * PATH: packages/tenant-shared/src/integrations/xeroStatusOAuth.ts
 * FILE_ID: TENANTSHARED-INTEGRATIONS-XEROSTATUS-OAUTH-001
 * PURPOSE: Start Xero OAuth and bridge popup callback completion.
 * WHY: Keep browser OAuth lifecycle separate from status polling.
 * FLOW: Click -> popup/redirect -> callback message -> status refresh.
 * DEPENDENCIES: Tenant API, notifications, status refresh event.
 * RELATED: useXeroStatus.ts, xeroStatusState.ts.
 */

import api from '../hooks/useApi'
import { notify } from '../stores/notificationStore'
import { XERO_STATUS_REFRESH_EVENT } from './xeroStatusState'

/**
 * Fetch the Xero authorize URL and open it for the user.
 *
 * Strategy:
 *   1. Try the popup approach first ? opens a small window so the
 *      user's main SAM tab is preserved even if Xero's login page
 *      returns ``ERR_INVALID_RESPONSE``. The backend's popup-mode
 *      callback ``postMessage``s the result back and self-closes,
 *      so the SAM card auto-refreshes without the user navigating.
 *   2. If the popup never actually navigates (host environment is
 *      embedded ? Cursor's IDE browser pane, VS Code Simple Browser,
 *      GitHub Codespaces preview, in-app webviews ? these all open
 *      the popup window but ignore subsequent ``popup.location.href``
 *      assignments), automatically close the dud popup and fall
 *      back to a full-page redirect. The backend's non-popup
 *      callback redirects back to ``return_to=/company/sam`` so the
 *      user lands on the same screen they started from.
 *
 * Critical implementation detail:
 *   The popup MUST be opened SYNCHRONOUSLY in the same task as the
 *   user's click ? i.e. before any ``await``. If we awaited the
 *   ``api.get(...)`` first, the browser's popup blocker would no
 *   longer treat ``window.open`` as triggered by a user gesture
 *   and would block it.
 *
 *   We open ``about:blank`` first (synchronously), then once the
 *   API call returns we set ``popup.location.href = url`` to load
 *   Xero's consent screen.
 *
 * Returns a promise that resolves to ``{ ok: true }`` on success
 * (popup-blocked / popup-frozen both still count as ok ? we fall
 * back to a full-page redirect) or ``{ ok: false, error }`` on
 * failure.
 */
export async function startXeroOAuth(
  options: { xeroTenantId?: string } = {},
): Promise<{ ok: true } | { ok: false, error: string }> {
  // SYNCHRONOUS: open the popup BEFORE any await so the browser still
  // counts this as a user-gesture-triggered open. Some browsers (Safari
  // especially) will silently block a window.open invoked after an
  // await, even from inside an async function called from an onClick.
  // about:blank is fine as the placeholder; we'll navigate it once the
  // authorize URL comes back.
  const popup = window.open(
    'about:blank',
    'quotech-xero-oauth',
    'popup=yes,width=620,height=720',
  )

  try {
    // Axios timeout slightly higher than sam-api's UI_TIMEOUT (8s) so the
    // server-side timeout response gets surfaced as a clean toast instead
    // of a client-side abort.
    //
    // ``xeroTenantId`` (optional): the Xero tenant UUID the user
    // intends to land active after the callback. Used by the per-row
    // "Reconnect this Xero account" button so an accountant
    // reauthorising customer A's Xero org doesn't accidentally
    // re-activate customer B's Xero org from the same OAuth grant.
    // See backend ``create_organisations_from_xero`` docstring for
    // the single-active-tenant invariant.
    //
    // ``popup=true`` flips the master backend's callback into
    // self-closing HTML + postMessage mode (vs the legacy redirect
    // mode that hijacks the user's tab). Required for the popup
    // window pattern to unwind cleanly.
    const params: Record<string, string> = { popup: 'true' }
    if (options.xeroTenantId) {
      params.xero_tenant_id = options.xeroTenantId
    }
    const { data } = await api.get<{ authorization_url: string }>('/xero/auth/connect', {
      timeout: 12_000,
      params,
    })
    if (!data?.authorization_url) {
      popup?.close()
      return { ok: false, error: 'Could not get Xero authorization URL' }
    }

    if (!popup || popup.closed) {
      // Popup blocker fired before we got the URL OR the user closed
      // the about:blank window before we could populate it. Fall
      // back to a fresh redirect-mode flow.
      return await doRedirectFallback(options)
    }

    // Navigate the popup to Xero's consent screen.
    try {
      popup.location.href = data.authorization_url
    } catch {
      // Some embedded browsers throw on cross-origin assignment
      // before navigation. Bail to redirect.
      popup.close()
      return await doRedirectFallback(options)
    }

    // ?? Embedded-browser detection ????????????????????????????????
    // In Cursor's IDE browser pane (and similar embedded webviews),
    // the popup window opens but ignores the location.href change ?
    // it just sits on its empty placeholder forever. Detect that by
    // polling popup.location.href after a short delay. In a normal
    // browser, by 1.5s the popup will have navigated to a Xero
    // origin and ``popup.location.href`` will throw a cross-origin
    // SecurityError (which is exactly the success signal ? we
    // swallow it and do nothing). If the access succeeds and still
    // returns ``about:blank``/empty, the host is embedded and we
    // fall back to a full-page redirect on the parent tab.
    const STUCK_POPUP_DETECTION_MS = 1500
    setTimeout(() => {
      if (popup.closed) return
      try {
        const href = popup.location?.href
        const stuck = !href || href === 'about:blank' || href === ''
        if (stuck) {
          popup.close()
          // Fire-and-forget ? the user has already moved past the
          // click handler. We need to navigate the parent tab to
          // continue the OAuth flow.
          void doRedirectFallback(options)
        }
      } catch {
        // Cross-origin access throw === popup successfully navigated
        // to xero.com. Nothing to do.
      }
    }, STUCK_POPUP_DETECTION_MS)

    return { ok: true }
  } catch (err) {
    popup?.close()
    const status = (err as { response?: { status?: number } })?.response?.status
    const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
    if (status === 504) {
      return {
        ok: false,
        error: detail ?? 'Connecting to Xero is taking longer than expected. Please try again in a moment.',
      }
    }
    if (status === 403) {
      return { ok: false, error: 'Only organisation owners or admins can connect Xero.' }
    }
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Failed to initiate Xero connection',
    }
  }
}

/**
 * Full-page redirect fallback for environments where the popup
 * approach can't work (popup blocker, embedded webview, etc.).
 *
 * Re-fetches the authorize URL WITHOUT ``popup=true`` so the master
 * backend's callback uses standard 302-redirect mode (back to
 * ``return_to=/company/sam``) instead of the postMessage + self-close
 * dance ? which would otherwise leave the user staring at the
 * "Finishing connection..." HTML because ``window.close()`` is
 * refused on tabs the script didn't open itself.
 */
async function doRedirectFallback(
  options: { xeroTenantId?: string } = {},
): Promise<{ ok: true } | { ok: false, error: string }> {
  try {
    const params: Record<string, string> = {}
    if (options.xeroTenantId) {
      params.xero_tenant_id = options.xeroTenantId
    }
    const { data } = await api.get<{ authorization_url: string }>('/xero/auth/connect', {
      timeout: 12_000,
      params,
    })
    if (!data?.authorization_url) {
      return { ok: false, error: 'Could not get Xero authorization URL' }
    }
    window.location.href = data.authorization_url
    return { ok: true }
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status
    const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
    if (status === 504) {
      return {
        ok: false,
        error: detail ?? 'Connecting to Xero is taking longer than expected. Please try again in a moment.',
      }
    }
    if (status === 403) {
      return { ok: false, error: 'Only organisation owners or admins can connect Xero.' }
    }
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Failed to initiate Xero connection',
    }
  }
}

// ---------------------------------------------------------------------------
// Popup callback bridge
// ---------------------------------------------------------------------------
//
// The master backend's popup-mode XeroCallbackView returns HTML that does
// ``window.opener.postMessage({ type: "xero-oauth-callback", payload })``
// just before ``window.close()``. We listen for that message at module
// scope (registered exactly once per SPA boot) and dispatch the existing
// ``XERO_STATUS_REFRESH_EVENT`` so every consumer of ``useXeroStatus`` /
// ``useXeroConnections`` re-fetches and surfaces the new connection state
// without the user having to navigate or refresh.
//
// Origin allow-list: only messages from the master backend's HTTPS origin
// are honoured. Anything else is silently dropped ? this listener is a
// global window.message handler so any other postMessage usage in the SPA
// (or any embedded iframe) MUST NOT be confused with a Xero callback.

const XERO_OAUTH_TRUSTED_ORIGINS = new Set([
  'https://quotech-backend.quotech.ai',
  'https://dashboard.quotech.ai',
])

interface QuotechXeroOAuthMessage {
  type: 'xero-oauth-callback'
  payload?: {
    connected?: boolean
    message?: string
    org_ids?: string
  }
}

function isXeroOAuthMessage(value: unknown): value is QuotechXeroOAuthMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'xero-oauth-callback'
  )
}

if (typeof window !== 'undefined') {
  // Idempotency guard: HMR reloads of this module would otherwise
  // stack listeners on every save in dev. Production loads this
  // exactly once but the guard is cheap and good hygiene.
  const w = window as Window & { __quotechXeroPopupBridgeInstalled?: boolean }
  if (!w.__quotechXeroPopupBridgeInstalled) {
    w.__quotechXeroPopupBridgeInstalled = true
    window.addEventListener('message', (event: MessageEvent) => {
      if (!XERO_OAUTH_TRUSTED_ORIGINS.has(event.origin)) return
      if (!isXeroOAuthMessage(event.data)) return
      // Fire the canonical refresh event ? every consumer of the
      // status hooks re-queries from this signal.
      window.dispatchEvent(new Event(XERO_STATUS_REFRESH_EVENT))

      // Surface a positive notification so the user sees an explicit
      // success signal (not just a silently-disappearing banner).
      // Lands in both the bottom-right toast AND the top-nav bell.
      const payload = event.data.payload ?? {}
      const ok = payload.connected !== false
      const description = payload.message
        ?? (payload.org_ids
              ? `Linked to Xero ? org_id=${payload.org_ids}`
              : 'Xero authorisation completed.')
      notify({
        title: ok ? 'Xero connected' : 'Xero connection failed',
        body: description,
        kind: ok ? 'success' : 'error',
        source: 'xero',
        cta: { label: 'Open Integrations', href: '/settings?tab=integrations' },
      })
    })
  }
}
